/**
 * Servicio de autenticación — Login, registro, logout y sincronización con Firebase
 * Exporta: authService
 */
import { logger } from "@/lib/logger";
import api from "./api";
import { unwrapApiData } from "@/lib/unwrapApiResponse";
import type { AuthResponse } from "@/types";
import { auth } from "@/config/firebase";
import { signInWithCustomToken, signInAnonymously } from "firebase/auth";
import toast from "react-hot-toast";

interface RegisterData {
  email: string;
  password: string;
  businessName: string;
  businessType?: string;
  planType?: string;
  document?: string;
  phone?: string;
  city?: string;
}

interface LoginData {
  email: string;
  password: string;
}

/**
 * Servicio de autenticación
 */
export const authService = {
  /**
   * Registrar nuevo negocio
   */
  register: async (data: RegisterData): Promise<AuthResponse> => {
    const response = await api.post<{ data: AuthResponse }>(
      "/auth/register",
      data,
    );
    return unwrapApiData(
      response.data,
      response.data as unknown as AuthResponse,
    );
  },

  /**
   * Iniciar sesión
   */
  login: async (data: LoginData): Promise<AuthResponse> => {
    const response = await api.post<{ data: AuthResponse }>(
      "/auth/login",
      data,
    );
    // Soportar tanto { data: { user, token } } (backend real / mock nuevo)
    // como { user, token } (respuesta plana de versiones anteriores del mock)
    const authData: AuthResponse =
      response.data.data ?? (response.data as unknown as AuthResponse);

    // Si recibimos un token de Firebase, iniciamos sesión en el SDK Cliente
    if (authData.firebaseToken) {
      try {
        await signInWithCustomToken(auth, authData.firebaseToken);
      } catch (error) {
        toast.error("Error conectando con servicios en la nube");
      }
    } else {
      // Modo demo/mock: no hay token real. Usar sesión anónima para que
      // Firebase Storage (y otras APIs de Firebase) funcionen correctamente.
      if (!auth.currentUser) {
        try {
          await signInAnonymously(auth);
          logger.log("✅ Sesión anónima de Firebase creada (modo demo)");
        } catch {
          // Si el anónimo no está habilitado en el proyecto, ignorar silenciosamente
        }
      }
    }

    return authData;
  },

  /**
   * Obtener datos del usuario autenticado
   */
  getMe: async (): Promise<unknown> => {
    const response = await api.get(`/auth/me?t=${Date.now()}`);
    return response.data;
  },

  /**
   * Cerrar sesión
   */
  logout: async (): Promise<void> => {
    try {
      await auth.signOut(); // Cerrar sesión en Firebase SDK también
      await api.post("/auth/logout");
    } catch (e) {
      // Ignorar error en logout
    }
  },

  /**
   * Solicita el restablecimiento de contraseña.
   * Llama al backend, que genera un token seguro y envía el email via Resend.
   */
  forgotPassword: async (email: string): Promise<void> => {
    await api.post("/auth/forgot-password", { email });
  },

  /**
   * Restablece la contraseña con el token recibido por email.
   * @param token      Token del query param ?token=xxx
   * @param newPassword Nueva contraseña del usuario
   */
  resetPassword: async (token: string, newPassword: string): Promise<void> => {
    await api.post("/auth/reset-password", { token, newPassword });
  },

  /**
   * Verifica si un token de reset es válido antes de mostrar el formulario.
   */
  verifyToken: async (
    token: string,
  ): Promise<{ valid: boolean; reason?: string }> => {
    const response = await api.post("/auth/verify-token", { token });
    return response.data;
  },
};
